import React from "react";
import { FiDollarSign, FiAlertTriangle, FiCheckCircle, FiDownload } from "react-icons/fi";
import { MiniBar } from "./waterHelpers";

const BILL_STATUS = {
  Paid:    { bg: "#d1fae5", color: "#059669" },
  Overdue: { bg: "#fee2e2", color: "#dc2626" },
  Pending: { bg: "#fef3c7", color: "#d97706" },
};

const WmBillingPanel = ({ d }) => {
  const b = d.billing;
  const maxCollected = Math.max(...b.collectionTrend.map(m => m.generated));

  return (
    <div className="wm-billing">

      {/* ── Summary KPI Row ── */}
      <div className="wb-summary-row">
        <div className="wb-kpi-card" style={{ borderTopColor: "#10b981" }}>
          <div className="wb-kpi-icon" style={{ background: "#d1fae5", color: "#059669" }}><FiCheckCircle size={18} /></div>
          <div className="wb-kpi-val" style={{ color: "#059669" }}>₹{(b.collected / 1000).toFixed(0)}k</div>
          <div className="wb-kpi-label">Collected</div>
          <div className="wb-kpi-sub">{b.collectionRate}% collection rate</div>
        </div>
        <div className="wb-kpi-card" style={{ borderTopColor: "#ef4444" }}>
          <div className="wb-kpi-icon" style={{ background: "#fee2e2", color: "#dc2626" }}><FiAlertTriangle size={18} /></div>
          <div className="wb-kpi-val" style={{ color: "#dc2626" }}>₹{(b.pending / 1000).toFixed(0)}k</div>
          <div className="wb-kpi-label">Pending</div>
          <div className="wb-kpi-sub">{b.overdueFlats} overdue flats</div>
        </div>
        <div className="wb-kpi-card" style={{ borderTopColor: "#6366f1" }}>
          <div className="wb-kpi-icon" style={{ background: "#e0e7ff", color: "#6366f1" }}><FiDollarSign size={18} /></div>
          <div className="wb-kpi-val" style={{ color: "#6366f1" }}>₹{(b.totalGenerated / 1000).toFixed(0)}k</div>
          <div className="wb-kpi-label">Total Generated</div>
          <div className="wb-kpi-sub">{b.month}</div>
        </div>
        <div className="wb-kpi-card" style={{ borderTopColor: "#f59e0b" }}>
          <div className="wb-kpi-icon" style={{ background: "#fef3c7", color: "#d97706" }}><FiAlertTriangle size={18} /></div>
          <div className="wb-kpi-val" style={{ color: "#d97706" }}>₹{(b.overdueAmount / 1000).toFixed(1)}k</div>
          <div className="wb-kpi-label">Overdue Amount</div>
          <div className="wb-kpi-sub">{b.overdueFlats} flats overdue</div>
        </div>
      </div>

      {/* ── Collection bar ── */}
      <div className="wb-collection-bar-card">
        <div className="wb-section-title">Collection Rate – {b.month}</div>
        <div className="wb-coll-row">
          <span>Collected ({b.collectionRate}%)</span>
          <div style={{ flex: 1, padding: "0 1rem" }}>
            <MiniBar value={b.collected} max={b.totalGenerated} color="#10b981" height={14} />
          </div>
          <span style={{ color: "#10b981", fontWeight: 600 }}>₹{(b.collected/1000).toFixed(0)}k</span>
        </div>
        <div className="wb-coll-row">
          <span>Pending ({(100 - b.collectionRate).toFixed(0)}%)</span>
          <div style={{ flex: 1, padding: "0 1rem" }}>
            <MiniBar value={b.pending} max={b.totalGenerated} color="#ef4444" height={14} />
          </div>
          <span style={{ color: "#ef4444", fontWeight: 600 }}>₹{(b.pending/1000).toFixed(0)}k</span>
        </div>
      </div>

      <div className="wb-layout">

        {/* ── Slab Pricing ── */}
        <div className="wb-slab-card">
          <div className="wb-section-title">Billing Structure</div>
          <div className="wb-slab-list">
            {b.slabs.map((slab, i) => (
              <div key={i} className="wb-slab-row">
                <div className="wb-slab-range">{slab.range}</div>
                <div className="wb-slab-rate">{slab.unit}</div>
              </div>
            ))}
          </div>
          <div className="wb-charges">
            <div className="wb-charge-row"><span>Fixed Monthly Charge</span><strong>₹{b.fixedCharge}</strong></div>
            <div className="wb-charge-row"><span>Tanker Cost (shared)</span><strong>₹{b.tankerCostShare.toLocaleString()}</strong></div>
            <div className="wb-charge-row"><span>GST</span><strong>{b.gstPct}%</strong></div>
            <div className="wb-charge-row"><span>Late Payment Penalty</span><strong>{b.latePenaltyPct}% per month</strong></div>
          </div>

          {/* Collection trend */}
          <div className="wb-section-title" style={{ marginTop: "1rem" }}>4-Month Collection Trend</div>
          <div className="wb-trend-chart">
            {b.collectionTrend.map(m => (
              <div key={m.month} className="wb-trend-col">
                <div className="wb-trend-wrap">
                  <div className="wb-trend-bar wb-generated" style={{ height: `${(m.generated / maxCollected) * 100}%` }} />
                  <div className="wb-trend-bar wb-collected" style={{ height: `${(m.collected / maxCollected) * 100}%` }} />
                </div>
                <span className="wb-trend-lbl">{m.month}</span>
              </div>
            ))}
          </div>
          <div className="wb-trend-legend">
            <span><span className="wb-leg-dot" style={{ background: "#e2e8f0" }} /> Generated</span>
            <span><span className="wb-leg-dot" style={{ background: "#10b981" }} /> Collected</span>
          </div>
        </div>

        {/* ── Sample Bills ── */}
        <div className="wb-bills-card">
          <div className="wb-section-title">Sample Bills – Feb 2026</div>
          <div className="wb-bills-table-wrap">
            <table className="wb-bills-table">
              <thead>
                <tr>
                  <th>Flat</th>
                  <th>Usage (KL)</th>
                  <th>Slab Amt</th>
                  <th>Fixed</th>
                  <th>Tanker</th>
                  <th>GST</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {b.sampleBills.map(bill => {
                  const ss = BILL_STATUS[bill.status] || BILL_STATUS.Pending;
                  return (
                    <tr key={bill.flatNo} className="wb-bill-row">
                      <td><strong>{bill.flatNo}</strong><br /><span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>Block {bill.block} · {bill.occupants} occ.</span></td>
                      <td>{bill.usage}</td>
                      <td>₹{bill.slabAmt.toLocaleString()}</td>
                      <td>₹{bill.fixed}</td>
                      <td>₹{bill.tanker}</td>
                      <td>₹{bill.gst}</td>
                      <td style={{ fontWeight: 700, color: "#1e293b" }}>₹{bill.total.toLocaleString()}</td>
                      <td><span className="wb-bill-status" style={{ background: ss.bg, color: ss.color }}>{bill.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button className="wb-export-btn">
            <FiDownload size={14} /> Export All Bills (Excel)
          </button>
        </div>

      </div>
    </div>
  );
};

export default WmBillingPanel;
